import { CATEGORY_SLUG_MAP, CATEGORY_SLUG_REVERSE_MAP } from "@/lib/constants";
import type { SearchFilters, SearchPageParams } from "./types";

/** Next.js `searchParams` values are `string | string[]` when keys repeat. */
export function joinQueryParam(
  v: string | string[] | undefined
): string | undefined {
  if (v === undefined) return undefined;
  if (Array.isArray(v)) return v.filter(Boolean).join(",");
  return v;
}

export function toCategorySlug(value: string): string {
  return (
    CATEGORY_SLUG_MAP[value] ||
    value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
  );
}

export function fromCategorySlug(slug: string): string {
  return CATEGORY_SLUG_REVERSE_MAP[slug] || slug;
}

export function toSlug(value: string): string {
  if (!value) return "";
  return value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

/** Match slug back to a canonical option string when possible. */
export function fromSlug(slug: string, availableOptions: string[] = []): string {
  if (!slug) return "";
  const exactMatch = availableOptions.find((opt) => toSlug(opt) === slug);
  if (exactMatch) return exactMatch;
  const caseMatch = availableOptions.find(
    (opt) =>
      opt.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") === slug
  );
  if (caseMatch) return caseMatch;
  return slug;
}

export function parseList(
  v: string | undefined,
  isCategory: boolean = false,
  availableOptions: string[] = []
): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => {
      const trimmed = s.trim();
      if (!trimmed) return null;
      return isCategory
        ? fromCategorySlug(trimmed)
        : fromSlug(trimmed, availableOptions);
    })
    .filter((s): s is string => s !== null);
}

function mergeCategoryParams(
  categoryRaw: string | undefined,
  productTypeRaw: string | undefined
): string[] {
  const a = categoryRaw ? parseList(categoryRaw, true) : [];
  const b = productTypeRaw ? parseList(productTypeRaw, true) : [];
  return [...new Set([...a, ...b])];
}

export function createInitialFiltersFromParams(
  params: SearchPageParams
): SearchFilters {
  const categoryJoined = joinQueryParam(params?.category);
  const productTypeJoined = joinQueryParam(params?.productType);

  return {
    q: (joinQueryParam(params?.q) || "").trim(),
    categories: mergeCategoryParams(categoryJoined, productTypeJoined),
    brands: parseList(joinQueryParam(params?.brand), false, []),
    collections: parseList(joinQueryParam(params?.collection), false, []),
    subCategories: parseList(joinQueryParam(params?.subCategory), false, []),
    sizes: parseList(joinQueryParam(params?.size), false, []),
    growingMethods: parseList(joinQueryParam(params?.growingMethod), false, []),
    types: parseList(joinQueryParam(params?.type), false, []),
    strains: parseList(joinQueryParam(params?.strain), false, []),
    dominantTerpenes: [
      ...parseList(joinQueryParam(params?.dominantTerpene), false, []),
      ...parseList(joinQueryParam(params?.terpene), false, []),
    ],
    flavors: parseList(joinQueryParam(params?.flavor), false, []),
    provincesOfOrigin: parseList(joinQueryParam(params?.provinceOfOrigin), false, []),
    regionsOfProduct: parseList(joinQueryParam(params?.regionOfProduct), false, []),
    extractionProcesses: parseList(joinQueryParam(params?.extractionProcess), false, []),
    carrierOils: parseList(joinQueryParam(params?.carrierOil), false, []),
    strainType: fromSlug((params?.strainType || "").trim(), []),
    onSale: String(params?.onSale || "").trim().toLowerCase() === "true" ? "true" : "",
    thcMin: (params?.thcMin || "").trim(),
    thcMax: (params?.thcMax || "").trim(),
    cbdMin: (params?.cbdMin || "").trim(),
    cbdMax: (params?.cbdMax || "").trim(),
    cbnMin: (params?.cbnMin || "").trim(),
    cbnMax: (params?.cbnMax || "").trim(),
    cbcMin: (params?.cbcMin || "").trim(),
    cbcMax: (params?.cbcMax || "").trim(),
    thcvMin: (params?.thcvMin || "").trim(),
    thcvMax: (params?.thcvMax || "").trim(),
    piecesMin: (params?.piecesMin || "").trim(),
    piecesMax: (params?.piecesMax || "").trim(),
    totalTerpeneMin: (params?.totalTerpeneMin || "").trim(),
    totalTerpeneMax: (params?.totalTerpeneMax || "").trim(),
    priceMin: (params?.priceMin || "").trim(),
    priceMax: (params?.priceMax || "").trim(),
    page: Math.max(1, Number(params?.page || "1") || 1),
    limit: Math.max(1, Number(params?.limit || "16") || 16),
    sortBy: (params?.sortBy || "recommended").trim(),
  };
}
